/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Image } from '@components/atoms'
import { LinearProgress } from '@mui/material'
import { Footer, Seo } from '@components/systems'
import { buildItemListJsonLd } from '@utils'

import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch, ApiGetDataStatus } from '@redux/index'
import { SeasonProductType } from '@schemas'
import { cyan, grey, yellow } from '@mui/material/colors'

import eventImg from '@public/images/pages/event/event2.jpg'
import kedem6Image from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery1.jpg'
import kedem4Image from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery1.jpg'
import amp2Image from '@public/images/pages/shop/amp2/gallery/amp2-gallery1.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery1.jpg'
import noImage from '@public/images/pages/shop/noimage.jpg'

const imageObj = {
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

const ShopPage = () => {
	// hook
	const navigate = useNavigate()
	const reduxDispatch = useAppDispatch()

	// redux state
	const status: ApiGetDataStatus = useAppSelector(state => state.shop.productListStatus)
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	const productList: SeasonProductType[] = React.useMemo(() => {
		const res = [...seasonProductList]

		res.sort((a, b) => {
			return a.productIndex < b.productIndex
				? -1
				: a.productIndex > b.productIndex
				? 1
				: 0
		})

		return res
	}, [seasonProductList])

	const onShopClick = (productCode: string) => {
		navigate(`/shop/${productCode.toLowerCase()}`)
	}

	// 라인업 구조화 데이터. 출시 전인 Koinos PLS 8 도 포함한다 — 티저 페이지가 색인 대상이다.
	const jsonLd = React.useMemo(
		() =>
			buildItemListJsonLd([
				{ name: 'Kedem 6', path: '/shop/kdm6' },
				{ name: 'Kedem 4', path: '/shop/kdm4' },
				{ name: 'AirMIDI POG 2', path: '/shop/amp2' },
				{ name: 'Judahs Dual Switch', path: '/shop/ds' },
				{ name: 'Koinos PLS 8', path: '/shop/pls8' },
			]),
		[],
	)

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			{/* <View className="eventView">
				<View className="eventInnerView">
					<Text className="eventTitle" category="h1">
						📢 Pre-Order Closing Soon!
					</Text>
					<ul className="eventUl">
						<li className="eventLi">⏰ Finish in May</li>
						<li className="eventLi">🚀 Shipping starts in July</li>
						<li className="eventLi">🚛 Free shipping – no extra cost!</li>
					</ul>

					<Text className="eventPrice">💥 Special Prices:</Text>
					<p className="eventPriceUnit">
						Kedem 6: <span className="eventNormalPrice">$407</span> ⮕{' '}
						<span className="eventDiscountedPrice">$369</span>
					</p>
					<p className="eventPriceUnit">
						Kedem 4: <span className="eventNormalPrice">$341</span> ⮕{' '}
						<span className="eventDiscountedPrice">$305</span>
					</p>
				</View>
			</View> */}
			{/* <View className="eventView">
				<Image src={eventImg} className="eventView" />
			</View> */}
			<View className="outerView">
				<View className="contentOuterView">
					<Text category="h1">SHOP</Text>

					<View className="contentView">
						{['init', 'loading'].includes(status) ? (
							<View className="loadingView">
								<LinearProgress className="loadingProgress" />
							</View>
						) : (
							<View className="contentInnerView">
								{productList.map(aProduct => {
									const stockStatus =
										aProduct.status === 'active' && aProduct.preorderYn === 1
											? 'preorder'
											: aProduct.status === 'active'
											? 'inStock'
											: 'soldout'
									const stockLabel =
										aProduct.status === 'active' && aProduct.preorderYn === 1
											? 'Pre-Order'
											: aProduct.status === 'active'
											? 'In Stock'
											: 'Sold Out'

									const imageKey = aProduct.productCode as keyof typeof imageObj
									const image = imageKey in imageObj ? imageObj[imageKey] : noImage

									return (
										<View key={aProduct.id} className="productOuterView">
											<Pressable
												className="productButton"
												onClick={() => onShopClick(aProduct.productCode)}
											>
												<View className="productImageView">
													<Image
														src={image}
														className="productImage"
														onLoad={event => {
															const imgElement = event.target as HTMLImageElement
															const isPortrait =
																imgElement.naturalHeight > imgElement.naturalWidth
															if (isPortrait) {
																imgElement.style.width = '100%'
																imgElement.style.height = 'auto'
															} else {
																imgElement.style.width = 'auto'
																imgElement.style.height = '100%'
															}
														}}
													/>
												</View>

												<View className={`stockLabel ${stockStatus}`}>
													<Text>{stockLabel}</Text>
												</View>

												<Text className="productName">{aProduct.productName}</Text>
												{aProduct.seasonDiscountUsd !== 0 ? (
													<>
														<Text className="canceledPrice">{`${aProduct.standardUsd.toLocaleString(
															'en-US',
															{
																style: 'currency',
																currency: 'USD',
															},
														)}`}</Text>
														<Text className="discountedPrice">{`${aProduct.seasonUsd.toLocaleString(
															'en-US',
															{
																style: 'currency',
																currency: 'USD',
															},
														)}`}</Text>
													</>
												) : (
													<Text className="productPrice">{`${aProduct.seasonUsd.toLocaleString(
														'en-US',
														{
															style: 'currency',
															currency: 'USD',
														},
													)}`}</Text>
												)}
											</Pressable>
										</View>
									)
								})}
							</View>
						)}
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default ShopPage

const style = css({
	position: 'relative',
	width: '100%',

	'.eventView': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		// padding: '2rem 4rem',
		// background: lime['A200'],

		'.eventImage': {
			width: '100%',
			height: 'auto',
			margin: 'auto',
			left: 0,
			right: 0,
			bottom: 0,
		},

		'.eventInnerView': {
			width: '24rem',
			color: grey[900],
			textAlign: 'left',

			'.eventTitle': {
				fontSize: '30px',
				marginTop: '0',
				marginBottom: '0',
			},

			'.eventUl': {
				'.eventLi': {
					fontSize: '20px',
					fontWeight: '800',
					marginLeft: '1rem',
					marginBottom: '0.5rem',
				},
			},

			'.eventPrice': {
				fontSize: '30px',
				marginTop: '2rem',
				marginBottom: '1rem',
			},

			'.eventPriceUnit': {
				marginLeft: '3rem',
				marginTop: '0',
				marginBottom: '0',
				fontSize: '20px',
				fontWeight: '800',

				'.eventNormalPrice': {
					marginLeft: '1rem',
					marginRight: '1rem',
					textDecoration: 'line-through',
					fontSize: '15px',
					fontWeight: '300',
				},
				'.eventDiscountedPrice': {
					marginLeft: '1rem',
					fontSize: '30px',
				},
			},
		},
	},

	'.outerView': {
		display: 'flex',
		width: '100%',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: '100%',
			padding: '2rem',

			'.contentView': {
				borderTop: '1px solid',
				borderColor: grey[500],

				'.loadingView': {
					width: '20rem',
					margin: 'auto',
					paddingTop: '3rem',
				},

				'.contentInnerView': {
					display: 'flex',
					flexWrap: 'wrap',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
					width: 'fit-content',
					maxWidth: '84rem',
					margin: '0 auto',
					padding: '3rem',
					boxSizing: 'border-box',

					'.productOuterView': {
						maxWidth: '42rem',
						width: 'calc(50% - 1rem)',
						marginBottom: '2rem',
						padding: '2rem',
						boxSizing: 'border-box',

						'.productButton': {
							width: '100%',

							'.productImageView': {
								display: 'flex',
								flex: '1 1 auto',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								overflow: 'hidden',
								width: '100%',
								height: '27rem',

								'.productImage': {
									width: '100%',
									heght: 'auto',
									objectFit: 'contain',
								},
							},

							'.productName': {
								margin: 0,
								marginTop: '1rem',
								fontSize: '30px',
								fontWeight: '500',
							},
							'.canceledPrice': {
								margin: 0,
								marginTop: '1rem',
								fontWeight: '300',
								textDecoration: 'line-through',
							},
							'.discountedPrice': {
								margin: 0,
								fontSize: '24px',
								fontWeight: '300',
							},
							'.productPrice': {
								margin: 0,
								marginTop: '1rem',
								fontSize: '24px',
								fontWeight: '300',
							},
							'.stockLabel': {
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '8rem',
								height: '2rem',
								margin: 'auto',
								marginTop: '1rem',
								padding: '0.05rem 1rem',
								background: grey[300],
								color: grey[500],
								borderRadius: '5px',

								'&.inStock': {
									background: yellow['A400'],
									color: grey[900],
								},

								'&.preorder': {
									background: cyan['A400'],
									color: grey[900],
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 1350px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.contentInnerView': {
						padding: '2rem 1rem',

						'.productOuterView': {
							padding: '0.5rem',
							width: '50%',

							'.productButton': {
								'.productImageView': {
									height: '35vw',
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 750px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.contentInnerView': {
						display: 'block',
						width: '100%',
						padding: '2rem 0',

						'.productOuterView': {
							width: '100%',
							marginBottom: '5rem',
							padding: 0,

							'.productButton': {
								'.productImageView': {
									height: '60vw',
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 530px)': {
		'.eventView': {
			padding: '2rem 1rem',

			'.eventInnerView': {
				width: '100%',
				'.eventTitle': {
					fontSize: '24px',
				},

				'.eventUl': {
					'.eventLi': {
						fontSize: '15px',
						fontWeight: '800',
						marginLeft: '1rem',
						marginBottom: '0.2rem',
					},
				},

				'.eventPrice': {
					fontSize: '24px',
					marginTop: '2rem',
					marginBottom: '1rem',
				},

				'.eventPriceUnit': {
					marginLeft: '3rem',
					marginTop: '0',
					marginBottom: '0',
					fontSize: '15px',
					fontWeight: '800',

					'.eventNormalPrice': {
						marginLeft: '1rem',
						marginRight: '1rem',
						textDecoration: 'line-through',
						fontSize: '12px',
						fontWeight: '300',
					},
					'.eventDiscountedPrice': {
						marginLeft: '1rem',
						fontSize: '15px',
					},
				},
			},
		},
	},

	'@media (max-width: 370px)': {
		'.eventView': {
			padding: '2rem 0 2rem 1rem',

			'.eventInnerView': {
				width: '19rem',
				'.eventTitle': {
					fontSize: '20px',
				},

				'.eventUl': {
					'.eventLi': {
						fontSize: '13px',
						fontWeight: '800',
						marginLeft: '0.5rem',
						marginBottom: '0.2rem',
					},
				},

				'.eventPrice': {
					fontSize: '20px',
					marginTop: '2rem',
					marginBottom: '1rem',
				},

				'.eventPriceUnit': {
					marginLeft: '2rem',
					marginTop: '0',
					marginBottom: '0',
					fontSize: '13px',
					fontWeight: '800',

					'.eventNormalPrice': {
						marginLeft: '0.5rem',
						marginRight: '0.5rem',
						textDecoration: 'line-through',
						fontSize: '12px',
						fontWeight: '300',
					},
					'.eventDiscountedPrice': {
						marginLeft: '0.5rem',
						fontSize: '13px',
					},
				},
			},
		},
	},
})
